/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    getProductName,
    sortAsc,
} from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
    TimeFrameOptions,
} from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { IntakeStatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router';

import { IntakeCasePausedIcon } from './IntakeCasePausedIcon';
import { ProductLabel } from './ProductLabel';

import { IntakeCaseGridActionsMenu } from 'components/intakeCases/IntakeCaseGridActionsMenu';
import { getIntakeStatusDetails } from 'components/shared/IntakeStatusDetails';
import { LighthouseIntakeStatusIndicator } from 'components/shared/LighthouseIntakeStatusIndicator';
import {
    IntakeCaseSnoozeStatus,
    Permissions,
    StatusDetailsDisplayStrings,
} from 'services/constants';
import { type TimeFrameOption } from 'services/interfaces';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetLighthouseUserNameMapQuery, useGetUserProfileQuery } from 'store/api/authApi';
import { type IntakeCaseSummary, useGetIntakeCasesQuery } from 'store/api/intakeCaseApi';
import { useGetProductsMapQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { type Grid, Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const timeFrameConfig = {
    key: 'create_date',
    title: 'Time Frame',
    defaultValue: TimeFrameOptions.PAST_MONTH,
};

const useColumns = (): ColumnDef<IntakeCaseSummary>[] => {
    const { showIntakeCasePauseOption } = useFlags<{ showIntakeCasePauseOption: boolean }>();
    const { data: segments } = useGetSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();
    const { data: userNameMap } = useGetLighthouseUserNameMapQuery();

    const getUserName = (email?: string) => (email ? userNameMap?.[email] : 'Unassigned');

    return [
        ...(showIntakeCasePauseOption
            ? [
                  {
                      accessorKey: 'paused_at',
                      header: () => <Icon icon={IconNames.NOTIFICATIONS} />,
                      size: 48,
                      cell: ({ row }) => <IntakeCasePausedIcon intakeCase={row.original} />,
                  } as ColumnDef<IntakeCaseSummary>,
              ]
            : []),
        {
            accessorKey: 'member_last_name',
            header: 'Patient',
            size: 150,
            cell: ({ getValue, row }) => (
                <PrimaryData
                    data={formatNameLastFirst(getValue(), row.original.member_first_name)}
                />
            ),
        },
        {
            accessorKey: 'member_id',
            header: 'Member ID',
            size: 120,
        },
        {
            accessorKey: 'product_service_oid',
            header: 'Product',
            size: 150,
            enableSorting: false,
            cell: ({ getValue, row }) => (
                <ProductLabel
                    product={getProductName(productsMap, getValue(), row.original.is_brand)}
                    isNonFormulary={row.original.non_formulary}
                />
            ),
        },
        {
            accessorKey: 'segment_id',
            header: 'Health Plan',
            size: 150,
            enableSorting: false,
            cell: ({ getValue }) => (
                <SecondaryData
                    data={segments?.find(({ segmentId }) => segmentId === getValue())?.name}
                />
            ),
        },
        {
            accessorKey: 'prescriber_last_name',
            header: 'Prescriber',
            size: 120,
            cell: ({ getValue, row }) => (
                <SecondaryData
                    data={formatNameLastFirst(getValue(), row.original.prescriber_first_name)}
                />
            ),
        },
        {
            accessorKey: 'create_date',
            header: 'Created Date',
            size: 150,
            cell: ({ getValue }) => (
                <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
            ),
        },
        {
            accessorKey: 'updated_date',
            header: 'Status Updated Date',
            size: 150,
            cell: ({ getValue }) => (
                <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
            ),
        },
        {
            accessorKey: 'assigned_username',
            header: 'Assignee',
            size: 150,
            cell: ({ getValue }) => <SecondaryData data={getUserName(getValue())} />,
        },
        {
            accessorKey: 'intake_status',
            header: 'Intake Status',
            size: 150,
            cell: ({ getValue }) => <LighthouseIntakeStatusIndicator status={getValue()} />,
        },
        {
            accessorKey: 'intake_status_details',
            header: 'Status Details',
            size: 150,
            cell: ({ getValue }) => <SecondaryData data={getIntakeStatusDetails(getValue())} />,
        },
        {
            accessorKey: 'intake_case_id',
            header: () => null,
            enableSorting: false,
            size: 48,
            cell: ({ getValue, row }) => (
                <IntakeCaseGridActionsMenu
                    intakeCase={{
                        intakeCaseId: getValue(),
                        intakeStatus: row.original.intake_status,
                        memberId: row.original.member_id,
                        memberFirstName: row.original.member_first_name,
                        memberLastName: row.original.member_last_name,
                        memberEligible: row.original.member_eligible,
                        isBrand: row.original.is_brand,
                        benefit: row.original.benefit,
                        productServiceOid: row.original.product_service_oid,
                        diagnosisCode: row.original.diagnosis_code,
                        prescriberVerified: row.original.prescriber_verified,
                        pausedAt: row.original.paused_at,
                        assignedUsername: row.original.assigned_username ?? '',
                    }}
                />
            ),
        },
    ];
};

// eslint-disable-next-line complexity
export const IntakeCaseGrid = ({ grid = Grids.ActiveIntakeCases }: { grid?: Grid }) => {
    const navigate = useNavigate();
    const permissions = useAppSelector((state) => state.auth.permissions);
    const isMonitoringGrid = grid === Grids.MonitoringIntakeCases;
    const pageTitle = isMonitoringGrid ? 'Monitoring Intake Cases' : 'Active Intake Cases';

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(grid, [{ id: 'create_date', desc: true }]);

    const apiQueryParams = {
        page,
        searchText: search,
        timeframe: activeFilters?.create_date?.[0] as TimeFrameOption,
        productServiceOid: activeFilters?.product_service_oid,
        segmentId: activeFilters?.segment_id,
        intakeStatus: (() => {
            if (activeFilters?.intake_status && activeFilters.intake_status.length > 0) {
                return activeFilters.intake_status;
            }
            return [
                IntakeStatusIndicator.Statuses.READY,
                IntakeStatusIndicator.Statuses.INCOMPLETE,
                IntakeStatusIndicator.Statuses.NEW,
                IntakeStatusIndicator.Statuses.PEND_RESPONSE,
                IntakeStatusIndicator.Statuses.PEND_SUBMIT,
                IntakeStatusIndicator.Statuses.PLAN_SET,
                IntakeStatusIndicator.Statuses.PRESCR_OUTREACH,
                IntakeStatusIndicator.Statuses.REVIEW_NEEDED,
            ];
        })(),
        authRequestStatus: activeFilters?.auth_request_status,
        intakeStatusDetails: activeFilters?.intake_status_details,
        intakeCaseSnoozeStatus:
            activeFilters?.intake_case_snooze_status as (typeof IntakeCaseSnoozeStatus)[keyof typeof IntakeCaseSnoozeStatus][],
        assignedUsername: activeFilters?.assigned_username,
        monitoringCase: isMonitoringGrid,
        sort,
    };
    // The filter pane reports the time frame's default value on mount, so activeFilters is
    // undefined for the first render. Wait for it rather than querying an unfiltered time frame
    // and refetching once the default lands.
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetIntakeCasesQuery(
        { ...apiQueryParams, page: 0 },
        { skip: !activeFilters },
    );
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } = useGetIntakeCasesQuery(
        apiQueryParams,
        { skip: !activeFilters },
    );

    const { data: segments } = useGetSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();
    const { data: profile } = useGetUserProfileQuery();
    const { data: tempUserNameMap } = useGetLighthouseUserNameMapQuery();
    const userNameMap = [
        ['unassigned', 'Unassigned'],
        [profile?.email ?? '', 'Assigned to me'],
        ...Object.entries(tempUserNameMap ?? {}).filter(([email, _]) => email !== profile?.email),
    ];

    const columns = useColumns();

    let filterCategories: FilterCategory<IntakeCaseSummary>[] = [
        {
            key: 'product_service_oid',
            title: 'Product',
            customValues: firstPageData?.filters
                ?.find((filter) => filter.key === 'product_service_oid')
                ?.values?.map((value) => ({
                    value,
                    text: getProductName(productsMap, value),
                })),
        },
        {
            key: 'segment_id',
            title: 'Health Plan',
            customValues: firstPageData?.filters
                ?.find((filter) => filter.key === 'segment_id')
                ?.values?.map((value) => ({
                    value,
                    text: segments?.find(({ segmentId }) => segmentId === value)?.name,
                }))
                .sort((a, b) => sortAsc(a.text, b.text)),
        },
    ];

    filterCategories = [
        {
            key: 'intake_case_snooze_status',
            title: 'Snooze',
            customValues: [
                {
                    value: IntakeCaseSnoozeStatus.ACTIVE,
                    text: 'Not Set',
                },
                {
                    value: IntakeCaseSnoozeStatus.INACTIVE,
                    text: 'Snoozed',
                },
                {
                    value: IntakeCaseSnoozeStatus.EXPIRED,
                    text: 'Ready for Follow-Up',
                },
            ],
        },
        ...filterCategories,
        {
            key: 'intake_status',
            title: 'Intake Status',
            customValues: Object.entries(IntakeStatusIndicator.StatusMessages)
                .filter(
                    ([value]) =>
                        value === IntakeStatusIndicator.Statuses.READY ||
                        value === IntakeStatusIndicator.Statuses.INCOMPLETE ||
                        value === IntakeStatusIndicator.Statuses.NEW ||
                        value === IntakeStatusIndicator.Statuses.PEND_RESPONSE ||
                        value === IntakeStatusIndicator.Statuses.PEND_SUBMIT ||
                        value === IntakeStatusIndicator.Statuses.PLAN_SET ||
                        value === IntakeStatusIndicator.Statuses.PRESCR_OUTREACH ||
                        value === IntakeStatusIndicator.Statuses.REVIEW_NEEDED,
                )
                .map(([value, text]) => ({ value, text })),
        },
        {
            key: 'intake_status_details',
            title: 'Status Details',
            customValues: Object.entries(StatusDetailsDisplayStrings).map(([value, text]) => ({
                value,
                text,
            })),
        },
        {
            key: 'assigned_username',
            title: 'Assignee',
            customValues: userNameMap
                ? userNameMap.map(([value, text]) => ({
                      value,
                      text,
                  }))
                : [],
        },
    ];
    const titleContent = (
        <div style={{ display: 'flex', alignItems: 'center', width: '100 %' }}>
            <span className="">{pageTitle}</span>

            {permissions.includes(Permissions.WRITE_INTAKE_CASE) && (
                <div style={{ marginLeft: 'auto ' }}>
                    <Button
                        intent="primary"
                        label="Intake Form"
                        onClick={() => navigate(AppRoutes.intakeForm)}
                    />
                </div>
            )}
        </div>
    );

    return (
        <FormPageContainer>
            <GridWithFilters
                key={grid}
                title={titleContent}
                data={currentPageData?.intakeCases}
                isLoading={!activeFilters || isFirstPageDataFetching || isCurrentPageDataFetching}
                columns={columns}
                manualSorting
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                timeFrameConfig={timeFrameConfig}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                onPageChange={updateCurrentPage}
                onRowClick={(row: IntakeCaseSummary) => {
                    navigate(AppRoutes.intakeCaseDetails(row.intake_case_id));
                }}
                totalCount={firstPageData?.count}
            />
        </FormPageContainer>
    );
};
